import React, { useState, useEffect, useRef } from "react";
import loaderAnimation from "../assets/animations/loader.json";

/**
 * A declarative React component for showing an activity indicator.
 * It is absolutely positioned and will cover its parent container.
 * The parent container should have `position: relative`.
 *
 * @param {object} props
 * @param {boolean} props.isLoading - Controls the visibility of the indicator.
 */
const WebActivityIndicatorComponent = ({ isLoading }) => {
    const [isShowing, setIsShowing] = useState(false);
    const showStartTime = useRef(0);
    const lottieContainerRef = useRef(null);
    const MIN_DISPLAY_TIME = 1500;

    // This effect handles the minimum display time to prevent flickering.
    useEffect(() => {
        if (isLoading) {
            showStartTime.current = Date.now();
            setIsShowing(true);
        } else if (isShowing) {
            const elapsed = Date.now() - showStartTime.current;
            const remaining = Math.max(0, MIN_DISPLAY_TIME - elapsed);

            const timer = setTimeout(() => {
                setIsShowing(false);
            }, remaining);

            return () => clearTimeout(timer);
        }
    }, [isLoading, isShowing]);

    // This effect handles loading the Lottie animation.
    useEffect(() => {
        if (isShowing && lottieContainerRef.current && typeof window.lottie !== "undefined") {
            try {
                window.lottie.loadAnimation({
                    container: lottieContainerRef.current,
                    renderer: "svg",
                    loop: true,
                    autoplay: true,
                    animationData: loaderAnimation,
                });
            } catch (error) {
                console.warn("Lottie failed to load, using fallback spinner");
            }
        }
    }, [isShowing]);

    if (!isShowing) {
        return null;
    }

    return (
        <div className="web-activity-indicator">
            <div className="web-activity-content">
                <div ref={lottieContainerRef} className="web-lottie-container">
                    {/* Fallback spinner for when Lottie fails */}
                    {typeof window.lottie === "undefined" && <div className="web-fallback-spinner"></div>}
                </div>
            </div>
        </div>
    );
};

export default WebActivityIndicatorComponent;
